import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlagsService } from '@atlassian/jira-flags';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { GroupContextField } from '../types.tsx';
import {
	getCreatedIssueFieldValue,
	getIssueCreateHiddenFlag,
	getIssueCreateRerankingPromptSuccessFlag,
	getIssueCreateSuccessFlag,
	getStatusName,
} from './utils.tsx';
import type { CreatedIssue } from './types.tsx';

export type ShouldSkipIssuesConnectionUpdate = (
	groupContextField?: GroupContextField,
	createdIssue?: CreatedIssue | null,
) => boolean;

// Temporary use of local storage has been approved here and will be removed with reranking changeboarding
export const storageProvider = createLocalStorageProvider(
	'jira-native-issue-table-inline-issue-create',
);

export const useShouldSkipIssuesConnectionUpdate = (): ShouldSkipIssuesConnectionUpdate => {
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(groupContextField?: GroupContextField, createdIssue?: CreatedIssue | null) => {
			if (!createdIssue) {
				return false;
			}

			// Determines whether to show the default success flag or the reranking prompt success flag
			const showDefaultSuccessFlag = (issueKey: string) => {
				const issueCreatedCount = storageProvider.get('issueCreatedCount') ?? 0;
				// TODO: JSC-1896 Activate only when table is not sorted by rank
				if (issueCreatedCount < 3) {
					storageProvider.set('issueCreatedCount', issueCreatedCount + 1);
					showFlag(getIssueCreateRerankingPromptSuccessFlag({ issueKey }));
				} else {
					showFlag(getIssueCreateSuccessFlag({ issueKey }));
				}
			};

			const issueKey = createdIssue.key;

			if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
				const event = createAnalyticsEvent({});

				const matchesIssueSearch = createdIssue.matchesIssueSearch !== false;
				const hasGroupValueChanged =
					!!groupContextField &&
					getCreatedIssueFieldValue(createdIssue, groupContextField.fieldId) !==
						groupContextField.fieldValue;

				if (!matchesIssueSearch) {
					issueKey && showFlag(getIssueCreateHiddenFlag({ issueKey }));
				} else if (hasGroupValueChanged && groupContextField?.fieldType === STATUS_TYPE) {
					issueKey &&
						showFlag(
							getIssueCreateSuccessFlag({
								issueKey,
								statusName: getStatusName(createdIssue),
							}),
						);
				} else if (fg('jsc_list_reranking_gate')) {
					issueKey && showDefaultSuccessFlag(issueKey);
				} else {
					issueKey && showFlag(getIssueCreateSuccessFlag({ issueKey }));
				}

				fireUIAnalytics(event, 'issueCreatedInListFlag shown', {
					matchesIssueSearch,
					groupIssueCreateFieldType: groupContextField?.fieldType || null,
					hasGroupValueChanged,
				});

				return hasGroupValueChanged || !matchesIssueSearch;
			}

			if (issueKey && fg('jsc_list_reranking_gate')) showDefaultSuccessFlag(issueKey);

			return false;
		},
		[showFlag, createAnalyticsEvent],
	);
};
